import { FontIcon, Icon, Spinner, SpinnerSize, Text, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { SharePointFolderContentTypeIds } from "../../../../../config/constants";
import { dropZoneStyleSets } from "../../../../../config/controlStyles";
import { ISharePointFolder } from "../../../../../interfaces/ISharePointFolder";
import { addUploadedFiles } from "../../../../../redux/modules/file";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { IFolderLocation } from "../../../../../interfaces/ISharepointDocument";
import { buildWorkingDirectoryFromTree, findWorkingDirectories, treeViewItemStyle } from "../../../../helpers/treeHelperGeneral";
import generalModule from "../../../../../../../utilities/GeneralModule";
import { RootState } from "../../../redux/storeMain";
import { ComponentTypes } from "../../../../../config/config";
import { OneDriveUrlHelper } from "../../../../helpers/oneDriveUrlHelper";
import { logger } from "../../../../../../../utilities/logger";

interface ISharePointFolderItemProps {
    item?: ISharePointFolder;
    treeItem?: IFolderLocation;
    isMobile: boolean;
    hasFileSelected: boolean;
    isValidDropZone: boolean;
    allowScroll?: boolean;
    getTreeLocations?: () => any[];
    onTreeItemClick?: (event: React.SyntheticEvent, treeItem: any) => Promise<void>;
    addFolderDirectory?: (item: ISharePointFolder) => Promise<void>;
    onSaveClicked: (item: any, e: any) => Promise<void>;
    openFolderOptions?: (folderInfo: ISharePointFolder) => void;
    openFolderOptionsTree?: (e: any) => void;
    onTreeExpand?: (isRefresh: boolean) => void;
}

export const SharePointFolderItem = ({ item, treeItem, isMobile, hasFileSelected, isValidDropZone, allowScroll, getTreeLocations, onTreeItemClick, addFolderDirectory, onSaveClicked, openFolderOptions, openFolderOptionsTree, onTreeExpand }: ISharePointFolderItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.user);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, treeItem)) {
            event.preventDefault();
            event.stopPropagation();
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, [treeItem]);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setIsDraggedOver(false);

        try {
            if (DragAndDropHelper.validateDroppedFiles(event)) {
                dispatch(addUploadedFiles(await DragAndDropHelper.handleDroppedFiles(event, treeItem)));
                onSaveClicked(item ? item : treeItem, event);
            }
            else if (DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, treeItem)) {
                const newWorkingDirectories = findWorkingDirectories(getTreeLocations(), treeItem);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const isInOneDrive = currDirectory.type == ComponentTypes.OneDrive || currDirectory.type == ComponentTypes.OneDriveSubFolder;
                const siteUrl = isInOneDrive ? OneDriveUrlHelper.getSiteUrl(currDirectory.webUrl) : currDirectory.webUrl

                DragAndDropHelper.destinationDirectory = currDirectory;
                DragAndDropHelper.handleGridMove(dispatch, siteUrl, directories, isInOneDrive);
            }
        } catch (error) {
            logger(error, "SharePointFolderItem (handleDrop)");
        }
    }, [item, treeItem, onSaveClicked]);

    const className = React.useMemo((): string => {
        let className: string = "list-item";
        if (treeItem) {
            className += ` ${"tree-item"}`;
            if (treeItem.isSelected) {
                className += ` ${"tree-item-selected"}`;
            }
        }

        if (isDraggedOver) {
            className += ` dropzone ${dropZoneStyleSets.dropzoneActive}`;
        }
        else if (isValidDropZone) {
            className += ` dropzone`;
        }
        return className;
    }, [isValidDropZone, treeItem, isDraggedOver]);

    const folderStyles: React.CSSProperties = isMobile ? mobileListItemStyle : {};

    const showChevron = React.useMemo((): boolean => {
        return treeItem && (((treeItem.canExpand && !treeItem.isLoading) || (treeItem.isLoading && treeItem.foldersNextHref)) &&
            !(treeItem.loaded && treeItem.folders?.length === 0));
    }, [treeItem]);

    React.useEffect(() => {
        let debounceTimer;
        if (treeItem && isValidDropZone && isDraggedOver && showChevron && onTreeExpand && !treeItem.expanded && !treeItem.isLoading) {
            debounceTimer = setTimeout(() => {
                onTreeExpand(false);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [treeItem, isValidDropZone, isDraggedOver, showChevron, onTreeExpand]);

    return (
        <div
            className={className}
            style={folderStyles}
            onDragEnter={isValidDropZone ? dragEnter : undefined}
            onDragOver={isValidDropZone ? dragOver : undefined}
            onDragLeave={isValidDropZone ? dragLeave : undefined}
            onDrop={isValidDropZone ? handleDrop : undefined}
            onClick={(e) => {
                e.stopPropagation();
                if (onTreeItemClick) {
                    onTreeItemClick(e, treeItem);
                }
                else if (addFolderDirectory) {
                    addFolderDirectory(item);
                }
            }}
        >
            <div className={!allowScroll ? "list-item-link" : "list-item-link-scrollable"}>
                {treeItem &&
                    <div style={treeViewItemStyle(treeItem)}
                        onClick={(e: any) => {
                            if (showChevron) {
                                e.stopPropagation();
                                onTreeExpand(false);
                            }
                        }}>
                        {treeItem.isLoading && !treeItem.foldersNextHref && <Spinner size={SpinnerSize.xSmall} />}
                        {showChevron && <FontIcon iconName={treeItem.expanded ? "ChevronDown" : "ChevronRight"} />}
                    </div>
                }
                <div className="list-item-icon">
                    <Icon {...getFileTypeIconProps({ type: (item || treeItem).contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder, size: 20, imageFileType: "svg" })} />
                </div>
                <Text className="list-item-name">
                    <TooltipHost content={(item || treeItem).name} overflowMode={TooltipOverflowMode.Parent}>
                        {(item || treeItem).name}
                    </TooltipHost>
                    {item && item.path && <div className="xb-grey-text text-ellipsis">
                        <TooltipHost
                            content={item.path}
                            overflowMode={TooltipOverflowMode.Parent}
                            style={{ maxWidth: "90vw", wordBreak: "break-all" }}
                        >
                            {item.path}
                        </TooltipHost>
                    </div>}
                </Text>
            </div>
            {treeItem && ((treeItem.canExpand && !treeItem.isLoading && treeItem.loaded) || (treeItem.canRefresh && !treeItem.isLoading)) && <TooltipHost content={languageStrings.Refresh}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    <FontIcon
                        iconName="Refresh"
                        className="refresh-icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            onTreeExpand(true);
                        }}
                    />
                </div>
            </TooltipHost>}
            {isValidDropZone && !(treeItem && treeItem?.isLoadingChannelInfo) && !generalModule.isInConentMangerMode() && (
                <TooltipHost content={languageStrings.SaveTo + (item || treeItem).name}>
                    <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                        <FontIcon
                            iconName="Save"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSaveClicked(item ? item : treeItem, e);
                            }}
                        />
                    </div>
                </TooltipHost>
            )}
            <TooltipHost content={languageStrings.MoreActions}>
                {(!treeItem || !treeItem?.isLoadingChannelInfo) && <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    <FontIcon
                        iconName="More"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item) {
                                openFolderOptions(item);
                            } else {
                                openFolderOptionsTree(e);
                            }
                        }}
                    />
                </div>}
                {treeItem && treeItem?.isLoadingChannelInfo && <Spinner size={SpinnerSize.xSmall} />}
            </TooltipHost>
        </div>
    )
};